import { useEffect, useState } from 'react';
import { useAppSelector } from './useRedux';
import { selectMenu } from '../features/infoSlice';

import {
  selectLibOptions,
  selectLibSelectedIds,
  libItemSelected,
  libDetailsToggled,
  selectLibSelectedId,
  selectLibSingleItem,
  libSortTypeChanged,
  libOrderChanged,
  libLayoutTypeChanged,
  libEditModeToggled,
  selectLibSelectedItems,
  selectLibProject,
  libIndexListChanged,
  libIndexIndentChanged,
  selectLibSelectedItem,
  libHistoryChanged,
  selectLibPage,
} from '../features/librarySlice';

import {
  selectShareOptions,
  selectShareSelectedIds,
  shareItemSelected,
  shareDetailsToggled,
  selectShareSelectedId,
  selectShareSingleItem,
  shareSortTypeChanged,
  shareOrderChanged,
  shareLayoutTypeChanged,
  shareEditModeToggled,
  selectShareSelectedItems,
  selectShareProject,
  shareIndexListChanged,
  shareIndexIndentChanged,
  selectShareSelectedItem,
  shareHistoryChanged,
  selectSharePage,
} from '../features/shareSlice';

import {
  selectDriveOptions,
  selectDriveSelectedIds,
  driveItemSelected,
  driveDetailsToggled,
  selectDriveSelectedId,
  selectDriveSingleItem,
  driveSortTypeChanged,
  driveOrderChanged,
  driveLayoutTypeChanged,
  driveEditModeToggled,
  selectDriveSelectedItems,
  selectDriveResource,
  driveIndexListChanged,
  driveIndexIndentChanged,
  selectDriveSelectedItem,
  selectDrivePage,
} from '../features/driveSlice';

import {
  selectSearchOptions,
  selectSearchSelectedIds,
  searchItemSelected,
  searchDetailsToggled,
  selectSearchSelectedId,
  selectSearchSingleItem,
  searchSortTypeChanged,
  searchOrderChanged,
  searchLayoutTypeChanged,
  searchEditModeToggled,
  selectSearchSelectedItems,
  selectSearchProject,
  searchIndexListChanged,
  searchIndexIndentChanged,
  selectSearchSelectedItem,
  selectSearchPage,
} from '../features/searchSlice';

import {
  selectTrashOptions,
  selectTrashSelectedIds,
  trashItemSelected,
  trashDetailsToggled,
  selectTrashSelectedId,
  selectTrashSingleItem,
  trashSortTypeChanged,
  trashOrderChanged,
  trashLayoutTypeChanged,
  trashEditModeToggled,
  selectTrashSelectedItems,
  selectTrash,
  trashIndexListChanged,
  trashIndexIndentChanged,
  selectTrashSelectedItem,
  selectTrashPage,
} from '../features/trashSlice';

import { selectAllLibProjects, selectProjectIds } from '../services/library';
import { selectAllShared, selectSharedIds } from '../services/share';
import { selectAllResource, selectResourceIds } from '../services/drive';
import { selectAllPublic, selectPublicIds } from '../services/search';
import { selectAllTrash, selectTrashIds } from '../services/trash';
import { RootState } from '../services/store';
import {
  Library,
  Options,
  Project,
  projectHistoryList,
  ProjectIndex,
} from '../types/library';
import { SharedLibrary } from '../types/share';
import {
  ActionCreatorWithOptionalPayload,
  ActionCreatorWithPayload,
  EntityId,
} from '@reduxjs/toolkit';
import { Resource } from '../types/drive';
import { SearchRes } from '../types/search';
import { Trash } from '../types/trash';
import { SortDirection, SortType } from '../types/common';

interface Slice {
  selectPage: (state: RootState) => number;
  selectIds: (state: RootState) => EntityId[];
  selectOptions: (state: RootState) => Options;
  selectSelectedIds: (state: RootState) => EntityId[];
  itemSelected: ActionCreatorWithPayload<
    {
      ids: EntityId[];
      item?:
        | Library
        | SharedLibrary
        | Resource
        | SearchRes
        | Trash
        | undefined
        | null;
    },
    string
  >;
  detailsToggled: ActionCreatorWithOptionalPayload<boolean | undefined, string>;
  selectSelectedId: (state: RootState) => EntityId | undefined;
  selectSingleItem: (
    state: RootState
  ) => Library | SharedLibrary | Resource | SearchRes | Trash | undefined;
  sortTypeChanged: ActionCreatorWithPayload<SortType, string>;
  orderChanged: ActionCreatorWithPayload<SortDirection, string>;
  layoutTypeChanged: ActionCreatorWithPayload<string, string>;
  editModeToggled: ActionCreatorWithPayload<boolean, string>;
  selectSelectedItems: (
    state: RootState
  ) => (Library | SharedLibrary | Resource | SearchRes | Trash | undefined)[];
  selectProject: (state: RootState) => Project | undefined;
  selectAll: (state: RootState) => (Library | SharedLibrary | Resource)[];
  indexListChanged: ActionCreatorWithPayload<ProjectIndex[], string>;
  indexIndentChanged: ActionCreatorWithPayload<number, string>;
  selectSelectedItem: (state: RootState) => Library | null;
  historyChanged: ActionCreatorWithPayload<projectHistoryList[], string> | null;
}

const librarySlice = {
  selectPage: selectLibPage,
  selectIds: selectProjectIds,
  selectOptions: selectLibOptions,
  selectSelectedIds: selectLibSelectedIds,
  itemSelected: libItemSelected,
  detailsToggled: libDetailsToggled,
  selectSelectedId: selectLibSelectedId,
  selectSingleItem: selectLibSingleItem,
  sortTypeChanged: libSortTypeChanged,
  orderChanged: libOrderChanged,
  layoutTypeChanged: libLayoutTypeChanged,
  editModeToggled: libEditModeToggled,
  selectSelectedItems: selectLibSelectedItems,
  selectProject: selectLibProject,
  selectAll: selectAllLibProjects,
  indexListChanged: libIndexListChanged,
  indexIndentChanged: libIndexIndentChanged,
  selectSelectedItem: selectLibSelectedItem,
  historyChanged: libHistoryChanged,
};
// TODO: Set type
const sliceByMenu: Record<string, Slice> = {
  login: librarySlice,
  main: librarySlice,
  classroom: librarySlice,
  settings: librarySlice,
  library: librarySlice,
  share: {
    selectPage: selectSharePage,
    selectIds: selectSharedIds,
    selectOptions: selectShareOptions,
    selectSelectedIds: selectShareSelectedIds,
    itemSelected: shareItemSelected,
    detailsToggled: shareDetailsToggled,
    selectSelectedId: selectShareSelectedId,
    selectSingleItem: selectShareSingleItem,
    sortTypeChanged: shareSortTypeChanged,
    orderChanged: shareOrderChanged,
    layoutTypeChanged: shareLayoutTypeChanged,
    editModeToggled: shareEditModeToggled,
    selectSelectedItems: selectShareSelectedItems,
    selectProject: selectShareProject,
    selectAll: selectAllShared,
    indexListChanged: shareIndexListChanged,
    indexIndentChanged: shareIndexIndentChanged,
    selectSelectedItem: selectShareSelectedItem,
    historyChanged: shareHistoryChanged,
  },
  mydrive: {
    selectPage: selectDrivePage,
    selectIds: selectResourceIds,
    selectOptions: selectDriveOptions,
    selectSelectedIds: selectDriveSelectedIds,
    itemSelected: driveItemSelected,
    detailsToggled: driveDetailsToggled,
    selectSelectedId: selectDriveSelectedId,
    selectSingleItem: selectDriveSingleItem,
    sortTypeChanged: driveSortTypeChanged,
    orderChanged: driveOrderChanged,
    layoutTypeChanged: driveLayoutTypeChanged,
    editModeToggled: driveEditModeToggled,
    selectSelectedItems: selectDriveSelectedItems,
    selectProject: selectDriveResource,
    selectAll: selectAllResource,
    indexListChanged: driveIndexListChanged,
    indexIndentChanged: driveIndexIndentChanged,
    selectSelectedItem: selectDriveSelectedItem,
    historyChanged: null,
  },
  search: {
    selectPage: selectSearchPage,
    selectIds: selectPublicIds,
    selectOptions: selectSearchOptions,
    selectSelectedIds: selectSearchSelectedIds,
    itemSelected: searchItemSelected,
    detailsToggled: searchDetailsToggled,
    selectSelectedId: selectSearchSelectedId,
    selectSingleItem: selectSearchSingleItem,
    sortTypeChanged: searchSortTypeChanged,
    orderChanged: searchOrderChanged,
    layoutTypeChanged: searchLayoutTypeChanged,
    editModeToggled: searchEditModeToggled,
    selectSelectedItems: selectSearchSelectedItems,
    selectProject: selectSearchProject,
    selectAll: selectAllPublic,
    indexListChanged: trashIndexListChanged,
    indexIndentChanged: searchIndexIndentChanged,
    selectSelectedItem: selectSearchSelectedItem,
    historyChanged: null,
  },
  trash: {
    selectPage: selectTrashPage,
    selectIds: selectTrashIds,
    selectOptions: selectTrashOptions,
    selectSelectedIds: selectTrashSelectedIds,
    itemSelected: trashItemSelected,
    detailsToggled: trashDetailsToggled,
    selectSelectedId: selectTrashSelectedId,
    selectSingleItem: selectTrashSingleItem,
    sortTypeChanged: trashSortTypeChanged,
    orderChanged: trashOrderChanged,
    layoutTypeChanged: trashLayoutTypeChanged,
    editModeToggled: trashEditModeToggled,
    selectSelectedItems: selectTrashSelectedItems,
    selectProject: selectTrash,
    selectAll: selectAllTrash,
    indexListChanged: searchIndexListChanged,
    indexIndentChanged: trashIndexIndentChanged,
    selectSelectedItem: selectTrashSelectedItem,
    historyChanged: null,
  },
};

export const useSlice = () => {
  const menu = useAppSelector(selectMenu) || 'login';
  const [slice, setSlice] = useState(sliceByMenu[menu]);

  useEffect(() => {
    setSlice(sliceByMenu[menu]);
  }, [menu]);

  return slice;
};
